// src/pages/EventDetailPage.js
// Page détail d'un événement — inscription, commentaires, inscrits
import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Globe,
  Users,
  Send,
  Pencil,
  Trash2,
  UserCircle,
  MessageCircle,
  AlertTriangle,
} from 'lucide-react';
import { Btn, Badge, Loading, Empty, ErrorState, ConfirmModal } from '../components/ui';
import { getCategoryLabel, CATEGORY_COLORS, CATEGORY_GRADIENTS } from '../constants';
import { timeAgo, formatDate, formatPrice } from '../utils';
import eventService from '../services/eventService';
import { trackEvent } from '../services/analyticsService';
import userService from '../services/userService';
import EventFormModal from '../modals/EventFormModal';

// ── Composant ──

const EventDetailPage = ({ user, openAuth, toast }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attendeeProfiles, setAttendeeProfiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  // Fetch événement
  const fetchEvent = useCallback(async () => {
    try {
      setError(false);
      setLoading(true);
      const ev = await eventService.getEvent(id);
      setEvent(ev);
    } catch (err) {
      console.error('EventDetailPage fetch error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEvent();
  }, [fetchEvent]);

  // Fetch avatars des 6 premiers inscrits
  useEffect(() => {
    if (!event?.attendees?.length) {
      setAttendeeProfiles([]);
      return;
    }
    const uids = event.attendees.slice(0, 6);
    Promise.all(uids.map((uid) => userService.getProfile(uid))).then((profiles) => {
      setAttendeeProfiles(profiles.filter(Boolean));
    });
  }, [event?.attendees]);

  // Souscription commentaires temps réel
  useEffect(() => {
    if (!id) return;
    const unsub = eventService.subscribeToComments(id, setComments);
    return () => unsub();
  }, [id]);

  // ── Actions ──

  const handleJoin = async () => {
    if (!user) {
      openAuth();
      return;
    }
    setJoining(true);
    const result = await eventService.joinEvent(id, user.uid);
    setJoining(false);
    if (result.success) {
      trackEvent('event_joined', { event_id: id });
      toast(t('eventDetail.toast.joinSuccess'), 'success');
      fetchEvent();
    } else {
      toast(result.error || t('eventDetail.toast.joinError'), 'error');
    }
  };

  const handleLeave = async () => {
    setLeaving(true);
    const result = await eventService.leaveEvent(id, user.uid);
    setLeaving(false);
    if (result.success) {
      toast(t('eventDetail.toast.leaveSuccess'), 'success');
      fetchEvent();
    } else {
      toast(result.error || t('eventDetail.toast.leaveError'), 'error');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim() || sendingComment) return;
    setSendingComment(true);
    const result = await eventService.addComment(id, {
      userId: user.uid,
      userName: user.name || '',
      userPhoto: user.profilePicture || '',
      text: commentText.trim(),
    });
    setSendingComment(false);
    if (result.success) {
      setCommentText('');
    } else {
      toast(result.error || t('eventDetail.toast.commentError'), 'error');
    }
  };

  const handleCancelEvent = async () => {
    if (!cancelTarget) return;
    const result = await eventService.cancelEvent(id, user.uid);
    setCancelTarget(null);
    if (result.success) {
      toast(t('eventDetail.toast.cancelled'), 'success');
      fetchEvent();
    } else {
      toast(result.error || t('eventDetail.toast.cancelError'), 'error');
    }
  };

  // ── États de chargement / erreur ──

  if (loading) return <Loading msg={t('eventDetail.loading')} />;

  if (error) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <ErrorState onRetry={fetchEvent} />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4">
        <Empty
          icon={Calendar}
          title={t('eventDetail.notFound.title')}
          desc={t('eventDetail.notFound.desc')}
          action={
            <Btn v="outline" icon={ArrowLeft} onClick={() => navigate('/events')}>
              {t('eventDetail.backToEvents')}
            </Btn>
          }
        />
      </div>
    );
  }

  // ── Dérivés ──

  const isOrganizer = user && event.organizerId === user.uid;
  const isAttendee = user && (event.attendees || []).includes(user.uid);
  const canComment = isOrganizer || isAttendee;
  const isCancelled = event.status === 'cancelled';
  const isFull = event.maxAttendees && (event.attendeeCount || 0) >= event.maxAttendees;
  const attendeeCount = event.attendeeCount || 0;
  const mapsUrl =
    event.location?.lat && event.location?.lng
      ? `https://www.google.com/maps/search/?api=1&query=${event.location.lat},${event.location.lng}`
      : null;

  const pageTitle = `${event.title || 'Événement'} — HmongHearts`;
  const pageDesc =
    (event.description || '').slice(0, 150) ||
    "Détail de l'événement sur HmongHearts, la communauté Hmong.";
  const ogImage = event.imageUrl || 'https://hmonghearts.com/og-default.png';

  const eventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: event.description || '',
    startDate: event.date?.toDate?.() ? event.date.toDate().toISOString() : undefined,
    endDate: event.endDate?.toDate?.() ? event.endDate.toDate().toISOString() : undefined,
    eventStatus:
      event.status === 'cancelled'
        ? 'https://schema.org/EventCancelled'
        : 'https://schema.org/EventScheduled',
    eventAttendanceMode: event.online
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    location: event.online
      ? { '@type': 'VirtualLocation', url: `https://hmonghearts.com/events/${id}` }
      : {
          '@type': 'Place',
          name: event.location?.name || event.location?.city || '',
          address: {
            '@type': 'PostalAddress',
            streetAddress: event.location?.address || '',
            addressLocality: event.location?.city || '',
            postalCode: event.location?.postalCode || '',
            addressCountry: 'FR',
          },
        },
    image: event.imageUrl || 'https://hmonghearts.com/og-default.png',
    organizer: {
      '@type': 'Organization',
      name: 'HmongHearts',
      url: 'https://hmonghearts.com',
    },
    offers: {
      '@type': 'Offer',
      price: event.price || 0,
      priceCurrency: 'EUR',
      availability:
        event.maxAttendees && (event.attendeeCount || 0) >= event.maxAttendees
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/InStock',
      url: `https://hmonghearts.com/events/${id}`,
    },
  };

  return (
    <article className="filigrane-kotaw pt-24 pb-32 lg:pb-20 container mx-auto px-4">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={`https://hmonghearts.com/events/${id}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={`https://hmonghearts.com/events/${id}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(eventJsonLd)}</script>
      </Helmet>
      <div className="max-w-3xl mx-auto">
        {/* Retour */}
        <button
          onClick={() => navigate('/events')}
          className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition font-medium text-sm"
        >
          <ArrowLeft size={18} />
          {t('eventDetail.backToEvents')}
        </button>

        {/* Hero image */}
        <div className="relative rounded-xl overflow-hidden mb-6">
          {event.imageUrl ? (
            <img
              src={event.imageUrl}
              alt={event.title}
              className="w-full h-48 sm:h-64 object-cover"
              fetchpriority="high"
            />
          ) : (
            <div
              className={`h-48 sm:h-64 bg-gradient-to-br ${CATEGORY_GRADIENTS[event.category] || 'from-gray-400 to-gray-500'} flex items-center justify-center`}
            >
              <Calendar size={64} className="text-white/20" />
            </div>
          )}
          <div className="absolute top-4 left-4 flex gap-2">
            <Badge c={CATEGORY_COLORS[event.category] || 'gray'}>
              {t(getCategoryLabel(event.category))}
            </Badge>
            {isCancelled && (
              <Badge c="gray">
                <span className="flex items-center gap-1">
                  <AlertTriangle size={12} />
                  {t('eventDetail.cancelled')}
                </span>
              </Badge>
            )}
          </div>
          {event.price > 0 && (
            <div className="absolute top-4 right-4 bg-white/90 dark:bg-gray-900/90 text-gray-900 dark:text-gray-100 text-sm font-bold px-3 py-1.5 rounded-full">
              {event.price} €
            </div>
          )}
        </div>

        {/* Titre + prix */}
        <h1 className="text-lg sm:text-2xl font-bold mb-2 dark:text-gray-100">{event.title}</h1>
        <div className="flex items-center gap-3 mb-5">
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            {formatPrice(event.price)}
          </span>
        </div>

        {/* Infos : date, lieu, organisateur */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 mb-6 space-y-3 shadow-sm">
          {/* Date */}
          <div className="flex items-start gap-3 text-sm">
            <Clock size={18} className="text-rose-500 flex-shrink-0 mt-0.5" />
            <div>
              <time
                dateTime={event.date?.toDate?.() ? event.date.toDate().toISOString() : ''}
                className="font-semibold dark:text-gray-100 block"
              >
                {formatDate(event.date)}
              </time>
              {event.endDate && (
                <time
                  dateTime={event.endDate?.toDate?.() ? event.endDate.toDate().toISOString() : ''}
                  className="text-gray-500 dark:text-gray-400 block"
                >
                  {t('eventDetail.untilDate', { date: formatDate(event.endDate) })}
                </time>
              )}
            </div>
          </div>

          {/* Lieu */}
          <div className="flex items-start gap-3 text-sm">
            {event.online ? (
              <>
                <Globe size={18} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <p className="font-semibold dark:text-gray-100">{t('common.online')}</p>
              </>
            ) : (
              <>
                <MapPin size={18} className="text-rose-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold dark:text-gray-100">
                    {event.location?.name || event.location?.city || t('common.locationTbc')}
                  </p>
                  {event.location?.address && (
                    <p className="text-gray-500 dark:text-gray-400">{event.location.address}</p>
                  )}
                  {mapsUrl && (
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-rose-500 hover:underline text-xs font-medium"
                    >
                      {t('common.seeOnGoogleMaps')}
                    </a>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Organisateur */}
          <div className="flex items-center gap-3 text-sm">
            <UserCircle size={18} className="text-rose-500 flex-shrink-0" />
            <div className="flex items-center gap-2">
              {event.organizerPhoto ? (
                <img
                  src={event.organizerPhoto}
                  alt={event.organizerName}
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold">
                  {(event.organizerName || '?')[0]}
                </div>
              )}
              <span className="font-semibold dark:text-gray-100">
                {event.organizerName || t('eventDetail.organizer')}
              </span>
              {isOrganizer && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {t('eventDetail.you')}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div className="mb-6">
            <h2 className="text-lg font-bold mb-2 dark:text-gray-100">
              {t('eventDetail.description')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
              {event.description}
            </p>
          </div>
        )}

        {/* Inscrits */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold dark:text-gray-100 flex items-center gap-2">
              <Users size={20} />
              {t('eventDetail.attendees.title')}
            </h2>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {attendeeCount} {t('common.registered').toLowerCase()}
              {attendeeCount !== 1 ? 's' : ''}
              {event.maxAttendees
                ? ` ${t('eventDetail.attendees.places', { max: event.maxAttendees })}`
                : ''}
            </span>
          </div>

          {attendeeCount === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-sm">
              {t('eventDetail.attendees.empty')}
            </p>
          ) : (
            <div className="flex items-center">
              <div className="flex -space-x-2">
                {attendeeProfiles.map((p) => (
                  <div key={p.uid} title={p.name}>
                    {p.profilePicture ? (
                      <img
                        src={p.profilePicture}
                        alt={p.name}
                        className="w-9 h-9 rounded-full object-cover border-2 border-white dark:border-gray-900"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold border-2 border-white dark:border-gray-900">
                        {(p.name || '?')[0]}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {attendeeCount > 6 && (
                <span className="ml-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                  +{attendeeCount - 6}
                </span>
              )}
            </div>
          )}

          {/* Bouton inscription / désinscription */}
          {!isCancelled && !isOrganizer && (
            <div className="mt-4">
              {isAttendee ? (
                <Btn
                  v="outline"
                  onClick={handleLeave}
                  loading={leaving}
                  className="w-full text-sm py-2.5"
                  data-testid="event-btn-leave"
                >
                  {t('eventDetail.actions.leave')}
                </Btn>
              ) : isFull ? (
                <Btn disabled className="w-full text-sm py-2.5" data-testid="event-btn-full">
                  {t('eventDetail.actions.full')}
                </Btn>
              ) : (
                <Btn
                  onClick={handleJoin}
                  loading={joining}
                  className="w-full text-sm py-2.5"
                  data-testid="event-btn-join"
                >
                  {t('eventDetail.actions.join')}
                </Btn>
              )}
            </div>
          )}

          {/* Actions organisateur */}
          {isOrganizer && !isCancelled && (
            <div className="mt-4 flex gap-3">
              <Btn
                v="outline"
                icon={Pencil}
                onClick={() => setFormOpen(true)}
                className="flex-1"
                data-testid="event-btn-edit"
              >
                {t('eventDetail.actions.edit')}
              </Btn>
              <Btn
                v="ghost"
                icon={Trash2}
                onClick={() => setCancelTarget(event)}
                className="flex-1 text-red-500 hover:text-red-600"
                data-testid="event-btn-cancel"
              >
                {t('eventDetail.actions.cancel')}
              </Btn>
            </div>
          )}
          {isOrganizer && (
            <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
              {t('eventDetail.attendees.organizerNote')}
            </p>
          )}
        </div>

        {/* Commentaires */}
        <div className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-sm">
          <h2 className="text-lg font-bold mb-4 dark:text-gray-100 flex items-center gap-2">
            <MessageCircle size={20} />
            {t('eventDetail.comments.title')}
            {comments.length > 0 && (
              <span className="text-sm font-normal text-gray-400 dark:text-gray-500">
                ({comments.length})
              </span>
            )}
          </h2>

          {/* Liste des commentaires */}
          {comments.length === 0 ? (
            <p className="text-gray-400 dark:text-gray-500 text-sm mb-4">
              {t('eventDetail.comments.empty')}
            </p>
          ) : (
            <div className="space-y-4 mb-4">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3">
                  {c.userPhoto ? (
                    <img
                      src={c.userPhoto}
                      alt={c.userName}
                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {(c.userName || '?')[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-semibold dark:text-gray-100">
                        {c.userName || t('conversation.defaultUser')}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {timeAgo(c.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 break-words">
                      {c.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Formulaire de commentaire */}
          {!user ? (
            <button
              onClick={openAuth}
              className="w-full text-center text-sm text-rose-500 hover:underline font-medium py-3"
            >
              {t('eventDetail.comments.loginPrompt')}
            </button>
          ) : canComment ? (
            <form onSubmit={handleComment} className="flex gap-2" data-testid="event-comment-form">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                aria-label={t('eventDetail.comments.placeholder')}
                placeholder={t('eventDetail.comments.placeholder')}
                maxLength={500}
                className="flex-1 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500/30 focus:border-rose-400"
                data-testid="event-comment-input"
              />
              <Btn
                type="submit"
                icon={Send}
                disabled={!commentText.trim()}
                loading={sendingComment}
                aria-label={t('eventDetail.comments.sendLabel')}
                className="px-4"
                data-testid="event-comment-submit"
              />
            </form>
          ) : (
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-3">
              {t('eventDetail.comments.joinPrompt')}
            </p>
          )}
        </div>
      </div>

      {/* Modal édition */}
      <EventFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        user={user}
        toast={toast}
        event={event}
        onSaved={() => {
          setFormOpen(false);
          fetchEvent();
        }}
      />

      {/* Modal confirmation annulation */}
      <ConfirmModal
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelEvent}
        title={t('eventDetail.cancelConfirm.title')}
        message={t('eventDetail.cancelConfirm.message', { title: event.title })}
      />
    </article>
  );
};

export default EventDetailPage;
